// SettingsPopup.js — Single slider for Snap Step (X & Y)
import React, { useState, useEffect } from 'react';
import './SettingsPopup.css';

const SettingsPopup = ({ onClose, isOpen }) => {
  const [activeTab, setActiveTab] = useState('display');

  // --- Display Grid (lines) ---
  const [showVisualGrid, setShowVisualGrid] = useState(true);
  const [visualGridSpacing, setVisualGridSpacing] = useState(80);
  const [gridColor, setGridColor] = useState('#5f7290');

  // --- Drawing Grid (dots + snapping) ---
  const [showDrawingGrid, setShowDrawingGrid] = useState(true);
  const [enableSnapping, setEnableSnapping] = useState(true);
  const [nmIdx, setNmIdx] = useState(0);          // SINGLE slider
  const [dotsColor, setDotsColor] = useState('#5f7290');
  const PX_PER_NM = 100;                 // 1 nm = 100 px (since 1 px = 0.01 nm)
const NM_STEPS = [0.01, 0.1, 1, 10];   // allowed nm steps

  // Load current values from engine when popup opens
  useEffect(() => {
    if (!isOpen) return;
    const gs = window.canvasEngine?.gridSystem;
    if (!gs) return;

    const { display, drawing } = gs;
    // inside useEffect after you read { display, drawing }
const px = (drawing.stepX ?? drawing.stepY ?? 1); // current step in px
const nm = px / PX_PER_NM;
let nearest = 0, best = Infinity;
NM_STEPS.forEach((v, i) => { const d = Math.abs(v - nm); if (d < best) { best = d; nearest = i; } });
setNmIdx(nearest);


    setActiveTab('display');
    setShowVisualGrid(!!display.show);
    setVisualGridSpacing(display.minPixelSpacing ?? 80);
    setGridColor(display.color ?? '#5f7290');

    setShowDrawingGrid(drawing.show ?? true);
    setEnableSnapping(!!drawing.enabled);

    // pick either stepX or stepY (both will be kept equal from now on)
    const step = (drawing.stepX ?? drawing.stepY ?? 1);
    

    setDotsColor(drawing.color ?? display.color ?? '#5f7290');
  }, [isOpen]);

  // --- Display handlers ---
  const handleShowVisualGrid = (e) => {
    const v = e.target.checked;
    setShowVisualGrid(v);
    window.canvasEngine?.gridSystem.setDisplayShow(v);
    window.canvasEngine?.draw();
  };
  const handleVisualSpacingChange = (e) => {
    const v = parseInt(e.target.value, 10);
    setVisualGridSpacing(v);
    window.canvasEngine?.gridSystem.setDisplaySpacing(v);
    window.canvasEngine?.draw();
  };
  const handleGridColorChange = (e) => {
    const color = e.target.value;
    setGridColor(color);
    window.canvasEngine?.gridSystem.setDisplayColor(color);
    window.canvasEngine?.draw();
  };

  // --- Drawing handlers ---
  const handleShowDrawingGrid = (e) => {
    const v = e.target.checked;
    setShowDrawingGrid(v);
    window.canvasEngine?.gridSystem.setDrawingShow(v);
    window.canvasEngine?.draw();
  };
  const handleEnableSnapping = (e) => {
    const v = e.target.checked;
    setEnableSnapping(v);
    window.canvasEngine?.gridSystem.setEnabled(v); // snapping only
    window.canvasEngine?.draw();
  };
  const handleSnapIndex = (e) => {
  const idx = Math.max(0, Math.min(3, parseInt(e.target.value, 10) || 0));
  setNmIdx(idx);
  const nm = NM_STEPS[idx];
  const px = nm * PX_PER_NM;              // 0.01→1px, 0.1→10px, 1→100px, 10→1000px
  window.canvasEngine?.gridSystem.setDrawingStep({ stepX: px, stepY: px });
  window.canvasEngine?.draw();
};

  const handleSnapBoth = (e) => {
    const v = Math.max(0.1, parseFloat(e.target.value) || 0.1);
   
    // set BOTH X and Y the same
    window.canvasEngine?.gridSystem.setDrawingStep({ stepX: v, stepY: v });
    window.canvasEngine?.draw();
  };
  const handleDotsColor = (e) => {
    const color = e.target.value;
    setDotsColor(color);
    window.canvasEngine?.gridSystem.setDrawingColor(color);
    window.canvasEngine?.draw();
  };

  if (!isOpen) return null;

  return (
    <div className="settings-popup">
      <div className="settings-header">
        <h3>Grid Settings</h3>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      {/* Tabs */}
      <div className="tab-bar">
        <button
          className={`tab-btn ${activeTab === 'display' ? 'active' : ''}`}
          onClick={() => setActiveTab('display')}
        >
          Display Grid
        </button>
        <button
          className={`tab-btn ${activeTab === 'drawing' ? 'active' : ''}`}
          onClick={() => setActiveTab('drawing')}
        >
          Drawing Grid
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'display' && (
          <div className="setting-group">
            <div className="setting-row">
              <label>Show Visual Grid</label>
              <label className="switch">
                <input type="checkbox" checked={showVisualGrid} onChange={handleShowVisualGrid} />
                <span className="slider round"></span>
              </label>
            </div>

            <div className="setting-row">
              <label>Major Line Spacing</label>
              <div className="slider-row">
                <input
                  type="range"
                  min="20"
                  max="200"
                  step="1"
                  value={visualGridSpacing}
                  onChange={handleVisualSpacingChange}
                />
                <span className="slider-value">{visualGridSpacing}px</span>
              </div>
            </div>

            <div className="setting-row">
              <label>Lines Color</label>
              <input type="color" value={gridColor} onChange={handleGridColorChange} />
            </div>
          </div>
        )}

        {activeTab === 'drawing' && (
          <div className="setting-group">
            <div className="setting-row">
              <label>Show Drawing Grid</label>
              <label className="switch">
                <input type="checkbox" checked={showDrawingGrid} onChange={handleShowDrawingGrid} />
                <span className="slider round"></span>
              </label>
            </div>

            <div className="setting-row">
              <label>Enable Snapping</label>
              <label className="switch">
                <input type="checkbox" checked={enableSnapping} onChange={handleEnableSnapping} />
                <span className="slider round"></span>
              </label>
            </div>

            {/* SINGLE slider controlling both X & Y */}
            <div className="setting-row">
  <label>Snap Step (X & Y)</label>
  <div className="slider-row">
    <input
      type="range"
      min="0"
      max="3"
      step="1"
      value={nmIdx}
      onChange={handleSnapIndex}
      list="nmStops"
    />
    <datalist id="nmStops">
      <option value="0" label="0.01" />
      <option value="1" label="0.1" />
      <option value="2" label="1" />
      <option value="3" label="10" />
    </datalist>
    <span className="slider-value">
      {NM_STEPS[nmIdx]} nm ({NM_STEPS[nmIdx] * PX_PER_NM}px)
    </span>
  </div>
</div>


            <div className="setting-row">
              <label>Dots Color</label>
              <input type="color" value={dotsColor} onChange={handleDotsColor} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPopup;
